/**
 * Telar Story – Object Data and Viewer Services
 *
 * This module provides the data layer and supporting services that IIIF
 * viewer plates need but that are not part of the plate lifecycle itself.
 * It sits between the raw data injected by Jekyll (window.objectsData,
 * window.storyData) and the card-pool module that orchestrates the visual
 * card stack.
 *
 * Object index — `buildObjectsIndex()` walks the objects array that Jekyll
 * injects at page load and builds a hash map keyed by object_id. Every
 * other module that needs object metadata (manifest URLs, credits, card
 * type detection) looks it up here via state.objectsIndex rather than
 * scanning the array each time.
 *
 * Manifest URLs — `getManifestUrl()` resolves an object's IIIF manifest.
 * Objects can be external (the source_url or iiif_manifest field from the
 * objects spreadsheet points to a remote IIIF server) or self-hosted (the
 * user placed image files in components/objects/ and the build-time script
 * generated static Level 0 tiles). For self-hosted objects, the function
 * builds a URL from the site's base path. Multi-page objects (PDFs) get a
 * per-page manifest URL when a page number is specified.
 *
 * Manifest prefetching — `prefetchStoryManifests()` fires off fetches for
 * every unique object's manifest at page load, warming the browser cache
 * so that IIIF wrapper instances created later do not wait on the network.
 * It also measures connection speed: if manifests take more than a second
 * on average, the loading threshold and minimum-ready counts are adjusted
 * so the shimmer state stays visible longer and does not flash.
 *
 * Loading shimmer — `initializeLoadingShimmer()` shows a skeleton pulse
 * on the viewer container while viewers initialise. It polls the viewer
 * card array until enough cards report isReady, then removes the shimmer.
 * Stories with fewer unique objects than the loading threshold skip the
 * shimmer entirely.
 *
 * Object credits badge — `initializeCredits()` wires the dismiss button
 * on the attribution badge, and `updateObjectCredits()` swaps the badge
 * text each time the active object changes. The badge stays hidden once
 * the user dismisses it.
 *
 * @version v1.6.0
 */

import { state } from './state.js';
import { getBasePath } from './utils.js';

// ── Object index ─────────────────────────────────────────────────────────────

/**
 * Build a lookup index from the objects data injected by Jekyll.
 *
 * Jekyll writes a global window.objectsData array containing one entry per
 * object in the story. This function converts that array into a hash map
 * on state.objectsIndex, keyed by object_id, so downstream code can look
 * up any object in O(1) without scanning the array.
 *
 * Called once during page initialisation by main.js.
 */
export function buildObjectsIndex() {
  const objects = window.objectsData || [];
  objects.forEach(obj => {
    state.objectsIndex[obj.object_id] = obj;
  });
}

// ── Manifest URLs ────────────────────────────────────────────────────────────

/**
 * Get the IIIF manifest URL for an object.
 *
 * Resolution order:
 *   1. External manifest — the source_url or iiif_manifest field from the
 *      objects spreadsheet. If present and non-empty, returned as-is.
 *   2. Local manifest — built from the site's base path and the object_id,
 *      pointing to the static tiles generated by scripts/generate_iiif.py.
 *
 * For multi-page objects (PDFs), when a page number is specified, the local
 * path points to the per-page single-canvas manifest rather than the root
 * multi-canvas manifest. External multi-page manifests are returned whole;
 * the caller (`card-pool.js _initOsdInPlate`) derives `startPage = page - 1`
 * and passes it to the IiifViewer wrapper, which opens the requested
 * canvas without needing per-page manifest variants on the external host.
 *
 * @param {string} objectId - The object identifier.
 * @param {number} [page] - Optional page number for multi-page objects.
 * @returns {string} The manifest URL.
 */
export function getManifestUrl(objectId, page) {
  const object = state.objectsIndex[objectId];

  if (!object) {
    console.warn('Object not found:', objectId);
    return buildLocalInfoJsonUrl(objectId, page);
  }

  const sourceUrl = object.source_url || object.iiif_manifest;
  if (sourceUrl && sourceUrl.trim() !== '') {
    return sourceUrl;  // External manifests: caller passes `page` to the wrapper via startPage
  }

  return buildLocalInfoJsonUrl(objectId, page);
}

/**
 * Build a local IIIF manifest URL from the site's base path.
 *
 * For single-image objects, returns the root manifest.json. For multi-page
 * objects (PDFs) with a page > 1 specified, returns the per-page manifest
 * at page-N/manifest.json — a single-canvas manifest that the viewer can
 * open directly without needing to navigate the full document.
 *
 * @param {string} objectId - The object identifier.
 * @param {number} [page] - Optional page number for multi-page objects.
 * @returns {string} Full URL to the local manifest.json.
 */
function buildLocalInfoJsonUrl(objectId, page) {
  const basePath = getBasePath();
  if (page) {
    const manifestUrl = `${window.location.origin}${basePath}/iiif/objects/${objectId}/page-${page}/manifest.json`;
    return manifestUrl;
  }
  const manifestUrl = `${window.location.origin}${basePath}/iiif/objects/${objectId}/manifest.json`;
  return manifestUrl;
}

// ── Manifest prefetching ─────────────────────────────────────────────────────

/**
 * Prefetch all story manifests at page load and measure connection speed.
 *
 * Iterates every unique object referenced by a [data-object] element in
 * the page. For each object with an external IIIF manifest, fires a cache-warming
 * fetch and records how long it takes so adjustThresholdsForConnection() can tune
 * the loading shimmer for slow networks.
 *
 * Resolves source_url || iiif_manifest (source_url is what the Compositor writes;
 * iiif_manifest is the legacy field) so Compositor-published stories get warmed
 * too. Video objects (whose source_url is a media host, not a manifest) are
 * skipped; audio objects use local files and have no external URL to warm.
 *
 * Runs as a background promise — does not block page initialisation.
 */
const _PREFETCH_SKIP_HOSTS = ['youtube.com', 'youtu.be', 'vimeo.com', 'drive.google.com'];

export async function prefetchStoryManifests() {
  const objectIds = [...new Set(
    Array.from(document.querySelectorAll('[data-object]'))
      .map(el => el.dataset.object)
      .filter(Boolean)
  )];

  if (objectIds.length === 0) return;

  const manifestUrls = [...new Set(
    objectIds
      .map(id => state.objectsIndex[id])
      .map(o => (o && (o.source_url || o.iiif_manifest) || '').trim())
      .filter(url => url && !_PREFETCH_SKIP_HOSTS.some(h => url.includes(h)))
  )];

  if (manifestUrls.length === 0) {
    adjustThresholdsForConnection();
    return;
  }

  // Warm the browser cache with bounded concurrency so a multi-object story does
  // not burst-open a connection per object. force-cache serves repeats from cache;
  // draining the body lets the connection free promptly.
  const CONCURRENCY = 3;
  const queue = [...manifestUrls];
  const worker = async () => {
    while (queue.length) {
      const url = queue.shift();
      try {
        const start = performance.now();
        const resp = await fetch(url, { cache: 'force-cache' });
        await resp.text().catch(() => {});
        state.manifestLoadTimes.push(performance.now() - start);
      } catch (e) { /* silent fail — network errors handled gracefully */ }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker)
  );

  adjustThresholdsForConnection();
}

/**
 * Adjust preloading thresholds based on measured connection speed.
 *
 * If the average manifest fetch time exceeds 1 second, the connection is
 * considered slow: the loading threshold drops to 1 (show shimmer for any
 * multi-object story) and the minimum ready count rises (wait for more
 * viewers before hiding shimmer). Moderate connections (500ms–1s) get a
 * gentler adjustment.
 *
 * This prevents the shimmer from flashing on fast connections while giving
 * slow connections enough visual feedback that the page does not appear
 * broken during the initial load.
 */
function adjustThresholdsForConnection() {
  if (state.manifestLoadTimes.length < 2) return;

  const avgTime = state.manifestLoadTimes.reduce((a, b) => a + b, 0) / state.manifestLoadTimes.length;

  if (avgTime > 1000) {
    state.config.loadingThreshold = 1;
    state.config.minReadyViewers = Math.min(6, state.config.preloadSteps);
  } else if (avgTime > 500) {
    state.config.loadingThreshold = Math.max(3, state.config.loadingThreshold - 2);
    state.config.minReadyViewers = Math.min(state.config.minReadyViewers + 1, state.config.preloadSteps);
  }
}

// ── Loading shimmer ──────────────────────────────────────────────────────────

/**
 * Show loading shimmer for stories with many unique viewers.
 *
 * Counts the unique objects in the story. If the count meets or exceeds
 * the loading threshold (adjusted by connection speed), shows a skeleton
 * pulse on the viewer container and polls state.viewerCards until enough
 * report isReady. Stories below the threshold skip the shimmer entirely —
 * their single viewer loads fast enough that a pulse would just flash.
 */
export function initializeLoadingShimmer() {
  const uniqueViewers = new Set(
    state.steps.map(step => step.dataset.object).filter(Boolean)
  ).size;

  if (uniqueViewers >= state.config.loadingThreshold) {
    showViewerSkeletonState();

    const checkReadyViewers = () => {
      const readyCount = state.viewerCards.filter(v => v.isReady).length;
      const targetReady = Math.min(state.config.minReadyViewers, uniqueViewers);

      if (readyCount >= targetReady) {
        hideViewerSkeletonState();
      } else {
        setTimeout(checkReadyViewers, 200);
      }
    };

    setTimeout(checkReadyViewers, 500);
  }
}

/**
 * Show the skeleton loading shimmer on the viewer container.
 *
 * Adds the skeleton-loading CSS class, which applies a pulsing gradient
 * animation over the viewer area. Used during initial load and when
 * switching to an object whose viewer is not yet ready.
 */
export function showViewerSkeletonState() {
  const container = document.getElementById('viewer-cards-container');
  if (container) {
    container.classList.add('skeleton-loading');
  }
}

/**
 * Hide the skeleton loading shimmer on the viewer container.
 */
export function hideViewerSkeletonState() {
  const container = document.getElementById('viewer-cards-container');
  if (container) {
    container.classList.remove('skeleton-loading');
  }
}

// ── Object credits badge ─────────────────────────────────────────────────────

/**
 * Set up the credits badge dismiss button.
 *
 * The credits badge sits in the bottom corner of the viewer area and shows
 * the attribution text for the current object. Once the user clicks the
 * dismiss button, the badge stays hidden for the rest of the session via
 * state.creditsDismissed.
 *
 * Only activates if the site has showObjectCredits enabled in _config.yml.
 */
export function initializeCredits() {
  if (!window.telarConfig?.showObjectCredits) return;

  const dismissBtn = document.getElementById('object-credits-dismiss');
  if (dismissBtn) {
    dismissBtn.addEventListener('click', function () {
      const badge = document.getElementById('object-credits-badge');
      if (badge) badge.classList.add('d-none');
      state.creditsDismissed = true;
    });
  }
}

/**
 * Update the credits badge to show the current object's attribution.
 *
 * Called by card-pool.js each time the active object changes. Looks up
 * the credit field from the object data; if present, displays it with
 * the localised prefix (e.g. "Credit:" in English, "Crédito:" in
 * Spanish). If the object has no credit or the user has dismissed the
 * badge, hides it.
 *
 * @param {string} objectId - The object whose credit to display.
 */
export function updateObjectCredits(objectId) {
  if (!window.telarConfig?.showObjectCredits) return;
  if (state.creditsDismissed) return;

  const badge = document.getElementById('object-credits-badge');
  const textElement = document.getElementById('object-credits-text');

  if (!badge || !textElement) return;

  const objectData = state.objectsIndex[objectId];
  const credit = objectData?.credit;

  if (credit && credit.trim()) {
    const prefix = window.telarLang?.creditPrefix || 'Credit:';
    textElement.textContent = `${prefix} ${credit}`;
    badge.classList.remove('d-none');
  } else {
    badge.classList.add('d-none');
  }
}
